import { IEventModule } from "app/event/model"
import { CampaignAPI, LookupAPI } from "generated-api"
import { Polygon } from "geojson"

export const MAX_POSTAL_CODE_LENGTH: number = 6

export type ILookupCityDTO = Awaited<ReturnType<typeof LookupAPI.getApiCityList>>[0]
export interface ILookupCity extends Omit<ILookupCityDTO, "id"> {
  city_shape_id: number
}
export type IGeoShapeCityDataDTO = Awaited<ReturnType<typeof LookupAPI.getApiCityShape>>
export interface IGeoShapeCityData extends Omit<IGeoShapeCityDataDTO, "id" | "shape"> {
  city_shape_id: number
  shape: Polygon
}
export type ICampaignCityShapeDTO = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignCityShapeList>>[0]
export type IGeoShapeCity = IGeoShape & IGeoShapeCityData
export type IGeoShapeStateShapeDTO = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignStateShapeList>>[0]
export type IGeoShapeCountryDTO = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignCountryShapeList>>[0]

export interface IGeoShapeCountryData {
  country_shape_id: string
  name: string
  alpha3: string
  longitude: number
  latitude: number
  shape: Polygon
}

export interface IGeoShapeStateData {
  country_id: string
  state_shape_id: string
  name: string
  shape: Polygon
}

export interface IGeoShapePostalCodeData {
  postal_code_shape_ids: number[]
  country_id: string
  state_id: string
  postal_code: string
  shape: Polygon
}

export enum SHAPE_EVENTS {
  SHAPES_ADD = "SHAPES_ADD",
  SHAPE_DELETE = "SHAPE_DELETE",
  SHAPES_DELETE = "SHAPES_DELETE",
  SHAPES_SET = "SHAPES_SET"
}

export enum SHAPE_TYPES {
  CITY = "CITY",
  POSTAL_CODE = "POSTAL_CODE",
  STATE = "STATE",
  COUNTRY = "COUNTRY",
  METROPOLITAN = "METROPOLITAN"
}

export const MIN_FILTER_LENGTH = 2

export interface IGeoShape {
  id: number
  shape: Polygon
  isProcessingServerSide?: boolean // creating, or deleting
  hasLoadingError?: boolean
  type: SHAPE_TYPES
}

export interface IGeoShapeMetropolitan extends IGeoShape {
  shape_id: string
  labelKey: string
  normalizedName: string
  cityIds: number[]
  cities: IGeoShapeCity[]
  state_id: string
  country_id: string
}

export type IGeoShapePostalCode = IGeoShape & IGeoShapePostalCodeData

export type IGeoShapeState = IGeoShapeStateData & IGeoShape

export type IGeoShapeCountry = IGeoShape & IGeoShapeCountryData

export interface IShapeRepository {
  // CITY
  searchCityByName(cityName: string): Promise<ILookupCity[]>
  getCitiesByNames(names: string[]): Promise<ILookupCity[]>
  getCityShapeData(cityId: number): Promise<IGeoShapeCityData>
  searchCities(filter: string): Promise<IGeoShapeCity[]>
  getAssociatedCities(campaignId: number): Promise<IGeoShapeCity[]>
  setAssociatedCities(campaignId: number, cities: IGeoShapeCity[]): Promise<IGeoShapeCity[]>
  associateCityToCampaign(campaignId: number, city: IGeoShapeCity): Promise<IGeoShapeCity>
  deassociateCityToCampaign(campaignId: number, cityAssociationId: number): Promise<void>
  // POSTAL CODE
  getPostalCodeData(postalCodeIds: number[], fallbackShape: IGeoShape): Promise<IGeoShapePostalCodeData>
  searchPostalCodes(filter: string): Promise<IGeoShapePostalCode[]>
  getPostalCodes(postalCodes: string[]): Promise<IGeoShapePostalCode[]>
  getAssociatedPostalCodes(campaignId: number): Promise<IGeoShapePostalCode[]>
  associatePostalCodeToCampaign(campaignId: number, postalCode: IGeoShapePostalCode): Promise<IGeoShapePostalCode>
  deassociatePostalCodeToCampaign(campaignId: number, postalCodeAssociationId: number): Promise<void>
  setPostalCodesToCampaign(campaignId: number, postalCodes: string[]): Promise<void>
  // STATE
  getStateData(stateId: string): Promise<IGeoShapeStateData>
  searchStates(filter: string): Promise<IGeoShapeState[]>
  getStatesById(ids: string[]): Promise<IGeoShapeState[]>
  getAssociatedStates(campaignId: number): Promise<IGeoShapeState[]>
  associateStateToCampaign(campaignId: number, state: IGeoShapeState): Promise<IGeoShapeState>
  deassociateStateToCampaign(campaignId: number, stateAssociationId: number): Promise<void>
  // COUNTRY
  getCountryData(countryId: string): Promise<IGeoShapeCountryData>
  searchCountries(filter: string): Promise<IGeoShapeCountry[]>
  getCountriesById(countryIds: string[]): Promise<IGeoShapeCountry[]>
  getAssociatedCountries(campaignId: number): Promise<IGeoShapeCountry[]>
  associateCountryToCampaign(campaignId: number, country: IGeoShapeCountry): Promise<IGeoShapeCountry>
  deassociateCountryToCampaign(campaignId: number, countryAssociationId: number): Promise<void>
}

export interface IShapeEventModule extends IEventModule {
  on: {
    onShapesAdd(listenerId: string, fn: (shapes: IGeoShape[]) => void): void
    onShapeDelete(listenerId: string, fn: (shape: IGeoShape) => void): void
    onShapesDelete(listenerId: string, fn: (shapes: IGeoShape[]) => void): void
  }
}

export interface IGetShapeTitleParams {
  shape: IGeoShape
  withType?: boolean
}

export interface IShapeOnSetParams {
  shapes: IGeoShape[]
  type: SHAPE_TYPES
}
